import {
  AUTH_COOKIE_KEYS,
  clearAllAuthCookies,
  deleteAuthCookie,
  getAuthCookie,
  getAuthCookieMaxAge,
  setAuthCookie,
} from './authCookieStorage';

const ACCESS_TOKEN_KEY = 'auth.accessToken';
const REFRESH_TOKEN_KEY = 'auth.refreshToken';
const TOKEN_TYPE_KEY = 'auth.tokenType';
const TOKEN_EXPIRES_AT_KEY = 'auth.tokenExpiresAt';
const USERNAME_KEY = 'auth.username';
const EMAIL_KEY = 'auth.email';
const GUEST_MODE_KEY = 'auth.guestMode';
const PERSISTENCE_MODE_KEY = 'auth.persistenceMode';

const PERSISTENCE_COOKIE = 'cookie';
const PERSISTENCE_SESSION = 'session';

// Klucz cache profilu z usersApi – trzymany tu, żeby uniknąć cyklicznej
// zależności authStorage ↔ usersApi. Wartość musi być zgodna z
// USER_PROFILE_CACHE_KEY w `usersApi.js`.
const USER_PROFILE_CACHE_KEY = 'user.profile.cache';
const VOYAGES_LIST_CACHE_PREFIX = 'voyages.my.list.';
const SAILING_GPS_ACTIVE_VOYAGE_KEY = 'ysh.sailingGpsLog.activeVoyage.v1';

const STORAGE_FIELD_MAP = [
  { storageKey: ACCESS_TOKEN_KEY, cookieKey: AUTH_COOKIE_KEYS.accessToken },
  { storageKey: REFRESH_TOKEN_KEY, cookieKey: AUTH_COOKIE_KEYS.refreshToken },
  { storageKey: TOKEN_TYPE_KEY, cookieKey: AUTH_COOKIE_KEYS.tokenType },
  { storageKey: TOKEN_EXPIRES_AT_KEY, cookieKey: AUTH_COOKIE_KEYS.tokenExpiresAt },
  { storageKey: USERNAME_KEY, cookieKey: AUTH_COOKIE_KEYS.username },
  { storageKey: EMAIL_KEY, cookieKey: AUTH_COOKIE_KEYS.email },
];

function clearCachedUserProfileLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(USER_PROFILE_CACHE_KEY);
  } catch {
    // Ignoruj private mode / quota.
  }
}

function clearActiveSailingGpsVoyageSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SAILING_GPS_ACTIVE_VOYAGE_KEY);
  } catch {
    // Ignoruj private mode / quota.
  }
}

function clearCachedVoyagesListLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(VOYAGES_LIST_CACHE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Ignoruj private mode / quota.
  }
}

function getSessionStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

function getLocalStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

export function getPersistenceMode() {
  const mode = getLocalStorage()?.getItem(PERSISTENCE_MODE_KEY);
  return mode === PERSISTENCE_SESSION ? PERSISTENCE_SESSION : PERSISTENCE_COOKIE;
}

export function setPersistenceMode(rememberMe) {
  const storage = getLocalStorage();
  if (!storage) return;
  storage.setItem(
    PERSISTENCE_MODE_KEY,
    rememberMe ? PERSISTENCE_COOKIE : PERSISTENCE_SESSION,
  );
}

function usesPersistentCookies() {
  return getPersistenceMode() === PERSISTENCE_COOKIE;
}

function readField(storageKey, cookieKey) {
  if (usesPersistentCookies()) {
    const fromCookie = getAuthCookie(cookieKey);
    if (fromCookie) return fromCookie;
  }
  return getSessionStorage()?.getItem(storageKey) ?? '';
}

function writeField(storageKey, cookieKey, value, maxAgeSec) {
  const session = getSessionStorage();
  if (usesPersistentCookies()) {
    session?.removeItem(storageKey);
    if (value) {
      setAuthCookie(cookieKey, value, maxAgeSec);
    } else {
      deleteAuthCookie(cookieKey);
    }
    return;
  }

  clearAllAuthCookies();
  if (value) {
    session?.setItem(storageKey, value);
  } else {
    session?.removeItem(storageKey);
  }
}

function clearLegacySessionAuthKeys() {
  const session = getSessionStorage();
  if (!session) return;
  STORAGE_FIELD_MAP.forEach(({ storageKey }) => session.removeItem(storageKey));
}

function migrateLegacySessionStorageToCookies() {
  if (!usesPersistentCookies()) return;
  const session = getSessionStorage();
  if (!session) return;

  const legacyToken = session.getItem(ACCESS_TOKEN_KEY);
  if (!legacyToken || getAuthCookie(AUTH_COOKIE_KEYS.accessToken)) return;

  STORAGE_FIELD_MAP.forEach(({ storageKey, cookieKey }) => {
    const value = session.getItem(storageKey);
    if (value) {
      setAuthCookie(cookieKey, value);
      session.removeItem(storageKey);
    }
  });
}

migrateLegacySessionStorageToCookies();

export function saveAuthSession(
  tokenPayload,
  username = '',
  email = '',
  { remember } = {},
) {
  if (remember !== undefined) {
    setPersistenceMode(remember);
  }

  const previousEmail = getAuthEmail();
  const previousUsername = getAuthUsername();

  const expiresInSec = Number(tokenPayload?.expires_in);
  const rawPreviousExpiry = readField(TOKEN_EXPIRES_AT_KEY, AUTH_COOKIE_KEYS.tokenExpiresAt);
  const previousExpiryMs = Number(rawPreviousExpiry);
  const expiresAt =
    Number.isFinite(expiresInSec) && expiresInSec > 0
      ? Date.now() + expiresInSec * 1000
      : Number.isFinite(previousExpiryMs) && previousExpiryMs > 0
        ? previousExpiryMs
        : Date.now();

  const previousRefresh = getRefreshToken();
  const incomingRefresh = tokenPayload?.refresh_token;
  const refreshToStore =
    typeof incomingRefresh === 'string' && incomingRefresh.length > 0
      ? incomingRefresh
      : previousRefresh;

  const incomingType = tokenPayload?.token_type;
  const typeToStore =
    typeof incomingType === 'string' && incomingType.length > 0
      ? incomingType
      : getTokenType() || 'Bearer';

  const cookieMaxAge = getAuthCookieMaxAge(expiresInSec);

  writeField(
    ACCESS_TOKEN_KEY,
    AUTH_COOKIE_KEYS.accessToken,
    tokenPayload?.access_token ?? '',
    cookieMaxAge,
  );
  writeField(REFRESH_TOKEN_KEY, AUTH_COOKIE_KEYS.refreshToken, refreshToStore, cookieMaxAge);
  writeField(TOKEN_TYPE_KEY, AUTH_COOKIE_KEYS.tokenType, typeToStore, cookieMaxAge);
  writeField(
    TOKEN_EXPIRES_AT_KEY,
    AUTH_COOKIE_KEYS.tokenExpiresAt,
    String(expiresAt),
    cookieMaxAge,
  );
  if (username) {
    writeField(USERNAME_KEY, AUTH_COOKIE_KEYS.username, username, cookieMaxAge);
  }
  if (email) {
    writeField(EMAIL_KEY, AUTH_COOKIE_KEYS.email, email, cookieMaxAge);
  }

  const session = getSessionStorage();
  session?.removeItem(GUEST_MODE_KEY);

  const accountChanged =
    (email && email !== previousEmail) ||
    (username && username !== previousUsername);
  if (accountChanged) {
    clearCachedUserProfileLocalStorage();
    clearCachedVoyagesListLocalStorage();
    clearActiveSailingGpsVoyageSession();
  }
}

export function clearAuthSession() {
  clearAllAuthCookies();
  clearLegacySessionAuthKeys();

  const session = getSessionStorage();
  session?.removeItem(GUEST_MODE_KEY);
  clearCachedUserProfileLocalStorage();
  clearCachedVoyagesListLocalStorage();
  clearActiveSailingGpsVoyageSession();
}

export function getAccessToken() {
  return readField(ACCESS_TOKEN_KEY, AUTH_COOKIE_KEYS.accessToken);
}

export function getRefreshToken() {
  return readField(REFRESH_TOKEN_KEY, AUTH_COOKIE_KEYS.refreshToken);
}

export function getTokenType() {
  return readField(TOKEN_TYPE_KEY, AUTH_COOKIE_KEYS.tokenType) || 'Bearer';
}

export function getAuthUsername() {
  return readField(USERNAME_KEY, AUTH_COOKIE_KEYS.username);
}

export function getAuthEmail() {
  return readField(EMAIL_KEY, AUTH_COOKIE_KEYS.email);
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

export function setGuestMode() {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.setItem(GUEST_MODE_KEY, '1');
  clearCachedUserProfileLocalStorage();
}

export function clearGuestMode() {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(GUEST_MODE_KEY);
}

export function isGuestMode() {
  return getSessionStorage()?.getItem(GUEST_MODE_KEY) === '1';
}

export function hasAppAccess() {
  return isAuthenticated() || isGuestMode();
}

const EXPIRY_BUFFER_MS = 30_000;

export function isTokenExpired() {
  const expiresAt = Number(
    readField(TOKEN_EXPIRES_AT_KEY, AUTH_COOKIE_KEYS.tokenExpiresAt),
  );
  if (!Number.isFinite(expiresAt) || expiresAt === 0) return false;
  return Date.now() >= expiresAt - EXPIRY_BUFFER_MS;
}
